import { useState } from 'react';

import { useAppRuntime } from '@/app/runtime';
import { useGame } from '@/store/game';
import { useBackButton } from '@/telegram';
import { MainAction } from '@/ui/MainAction';

import { DeckPicker } from './DeckPicker';
import { IntensityPicker } from './IntensityPicker';
import { ROOM_CODE_RE, describeError, describeJoinError } from './shared';

export interface MultiSetupProps {
  onBack: () => void;
}

type Step = 'choose' | 'create' | 'join';

/**
 * multi: создать комнату либо войти по коду. Имя и пол здесь не заводит
 * хост — контракт не даёт клиенту способа сообщить чужие имя/пол (ни одной
 * ручки под это ни в REST, ни в WS-командах), значит участник представлен
 * своей сессией, а не формой. Как только он подключится, он появится в
 * PublicState.players сам — это уже экран ConnectedLobby.
 */
export function MultiSetup({ onBack }: MultiSetupProps) {
  const [step, setStep] = useState<Step>('choose');

  if (step === 'create') return <CreateRoom onBack={() => setStep('choose')} />;
  if (step === 'join') return <JoinByCode onBack={() => setStep('choose')} />;
  return <ChooseStep onBack={onBack} onCreate={() => setStep('create')} onJoin={() => setStep('join')} />;
}

function ChooseStep({
  onBack,
  onCreate,
  onJoin,
}: {
  onBack: () => void;
  onCreate: () => void;
  onJoin: () => void;
}) {
  useBackButton(onBack);
  const restClient = useAppRuntime()?.restClient ?? null;

  return (
    <div
      className="flex min-h-screen flex-col justify-center gap-3 bg-ground px-5 text-ink"
      style={{ paddingTop: 'var(--clown-safe-top)', paddingBottom: 'var(--clown-safe-bottom)' }}
    >
      <h1 className="mb-3 font-display text-task">У КАЖДОГО СВОЙ</h1>

      {!restClient && <p className="text-base text-acc">Недоступно офлайн.</p>}

      <button
        type="button"
        onClick={onCreate}
        disabled={!restClient}
        className="min-h-tap appearance-none rounded-none border-2 border-line bg-panel p-4 text-left disabled:opacity-40"
      >
        <div className="font-display text-row">Создать комнату</div>
        <div className="mt-1 text-lead text-dim">Стать хостом, позвать остальных кодом</div>
      </button>

      <button
        type="button"
        onClick={onJoin}
        disabled={!restClient}
        className="min-h-tap appearance-none rounded-none border-2 border-line bg-panel p-4 text-left disabled:opacity-40"
      >
        <div className="font-display text-row">Войти по коду</div>
        <div className="mt-1 text-lead text-dim">Комнату уже создал кто-то другой</div>
      </button>
    </div>
  );
}

function CreateRoom({ onBack }: { onBack: () => void }) {
  useBackButton(onBack);
  const runtime = useAppRuntime();
  const [maxIntensity, setMaxIntensity] = useState(3);
  const [deckVersion, setDeckVersion] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleCreate(): Promise<void> {
    if (!runtime?.restClient || pending) return;
    setPending(true);
    setError(null);
    try {
      const room = await runtime.restClient.createRoom({
        roomMode: 'multi',
        gameMode: 'free',
        maxIntensity,
        // Поля нет, пока витрина не ответила: версию тогда выбирает сервер
        // (то же, что в HotseatSetup).
        ...(deckVersion === null ? {} : { deckVersion }),
      });
      await runtime.connectToRoom(room.code);
    } catch (err) {
      setError(describeError(err));
      setPending(false);
    }
  }

  return (
    <div
      className="flex min-h-screen flex-col bg-ground px-5 text-ink"
      style={{ paddingTop: 'var(--clown-safe-top)' }}
    >
      <div className="flex-1 py-6">
        <h1 className="font-display text-task">НОВАЯ КОМНАТА</h1>
        <IntensityPicker value={maxIntensity} onChange={setMaxIntensity} />
        <DeckPicker value={deckVersion} onChange={setDeckVersion} />
        {!runtime?.restClient && (
          <p className="mt-4 text-base text-acc">Создание комнаты недоступно офлайн.</p>
        )}
      </div>
      {/* Отказ стоит у кнопки: MainButton нативная, а форма длиннее экрана. */}
      <div className="-mx-5" style={{ paddingBottom: 'var(--clown-safe-bottom)' }}>
        {error && <p className="mx-5 mb-2 text-base text-acc">{error}</p>}
        {!error && pending && <p className="mx-5 mb-2 text-base text-mute">Создаём комнату…</p>}
        <MainAction
          text="СОЗДАТЬ КОМНАТУ"
          enabled={!!runtime?.restClient && !pending}
          onPress={handleCreate}
        />
      </div>
    </div>
  );
}

function JoinByCode({ onBack }: { onBack: () => void }) {
  useBackButton(onBack);
  const runtime = useAppRuntime();
  const [code, setCode] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const valid = ROOM_CODE_RE.test(code);

  async function handleJoin(): Promise<void> {
    if (!runtime?.restClient || !valid || pending) return;
    setPending(true);
    setError(null);
    try {
      // Ранняя проверка кода через REST — иначе об опечатке узнаём только на
      // отказе WS-хендшейка, что выглядит как обрыв связи, а не как ошибка ввода.
      const room = await runtime.restClient.getRoom(code);
      // hotseat-комнату по WS открывает только хост, и отказ приходит кодом
      // `unauthorized` с текстом про недействительную сессию — про сессию там
      // ни слова правды. Отвечаем сами, пока не соврал сервер.
      if (room.roomMode === 'hotseat') {
        setError('Это комната на один телефон: в неё входит только хост');
        setPending(false);
        return;
      }
      await runtime.connectToRoom(code);
    } catch (err) {
      setError(describeJoinError(err, useGame.getState().fatal));
      setPending(false);
    }
  }

  return (
    <div
      className="flex min-h-screen flex-col bg-ground px-5 text-ink"
      style={{ paddingTop: 'var(--clown-safe-top)' }}
    >
      <div className="flex-1 py-6">
        <h1 className="font-display text-task">КОД КОМНАТЫ</h1>
        <input
          value={code}
          onChange={(event) => setCode(event.target.value.toUpperCase().slice(0, 4))}
          placeholder="ABCD"
          aria-label="Код комнаты"
          className="mt-4 h-tap w-full appearance-none rounded-none border-2 border-line bg-panel px-4 text-center font-display text-display uppercase text-ink outline-none focus:border-acc"
        />
        {!runtime?.restClient && (
          <p className="mt-4 text-base text-acc">Вход по коду недоступен офлайн.</p>
        )}
      </div>
      <div className="-mx-5" style={{ paddingBottom: 'var(--clown-safe-bottom)' }}>
        {error && <p className="mx-5 mb-2 text-base text-acc">{error}</p>}
        {!error && !valid && (
          <p className="mx-5 mb-2 text-base text-mute">Код — четыре знака: буквы и цифры.</p>
        )}
        <MainAction
          text="ВОЙТИ"
          enabled={valid && !!runtime?.restClient && !pending}
          onPress={handleJoin}
        />
      </div>
    </div>
  );
}
